import type {GeoFix} from './geo'
import {geoDistanceMeters} from './geo'

export interface GpsTracker {
  /** Latest raw fix (accuracy-unfiltered), or null before first fix. */
  readonly last: GeoFix | null
  /** Latest fix that passed the accuracy gate, or null. */
  readonly lastGood: GeoFix | null
  /**
   * Latest good fix, but only once the last STABLE_STREAK good fixes agree within
   * STABLE_SPREAD_M of each other (Unity-style gate: a single "accurate" first fix
   * is often stale/wrong — require consecutive agreeing fixes before trusting).
   */
  readonly stableFix: GeoFix | null
  /** Number of consecutive good fixes currently agreeing (debug). */
  readonly goodStreak: number
  readonly error: string | null
  stop: () => void
}

export const GPS_ACCURACY_GATE_M = 25
export const STABLE_STREAK = 3
export const STABLE_SPREAD_M = 15

/**
 * Start a high-accuracy geolocation watch. Triggers the browser's location
 * permission prompt — call only after the camera is up (plan §2 permission order).
 * Supports a `?fakegps=lat,lng[,accuracy]` override for testing at a desk.
 */
export function startGps(onUpdate?: (fix: GeoFix) => void): GpsTracker {
  let last: GeoFix | null = null
  let lastGood: GeoFix | null = null
  let error: string | null = null
  let watchId: number | null = null
  let fakeTimer: ReturnType<typeof setInterval> | null = null
  // Rolling window of recent GOOD fixes; the streak resets whenever a new good
  // fix disagrees with the window (jumpy signal → start over).
  let streak: GeoFix[] = []

  const acceptGood = (fix: GeoFix) => {
    lastGood = fix
    const agrees = streak.every((f) => geoDistanceMeters(f, fix) <= STABLE_SPREAD_M)
    streak = agrees ? [...streak.slice(-(STABLE_STREAK * 2)), fix] : [fix]
  }

  const fake = new URLSearchParams(window.location.search).get('fakegps')
  if (fake) {
    const [lat, lng, acc] = fake.split(',').map(Number)
    const tick = () => {
      const fix: GeoFix = {
        lat,
        lng,
        accuracy: Number.isFinite(acc) ? acc : 5,
        timestamp: Date.now(),
      }
      last = fix
      if (fix.accuracy <= GPS_ACCURACY_GATE_M) acceptGood(fix)
      onUpdate?.(fix)
    }
    tick()
    fakeTimer = setInterval(tick, 1000)
  } else if (!('geolocation' in navigator)) {
    error = 'Geolocation is not available in this browser.'
  } else {
    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const fix: GeoFix = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: pos.timestamp,
        }
        last = fix
        if (fix.accuracy <= GPS_ACCURACY_GATE_M) acceptGood(fix)
        error = null
        onUpdate?.(fix)
      },
      (err) => {
        error =
          err.code === err.PERMISSION_DENIED
            ? 'Location permission denied.'
            : `Location error: ${err.message}`
      },
      {enableHighAccuracy: true, maximumAge: 0, timeout: 30000},
    )
  }

  return {
    get last() {
      return last
    },
    get lastGood() {
      return lastGood
    },
    get stableFix() {
      return streak.length >= STABLE_STREAK ? streak[streak.length - 1] : null
    },
    get goodStreak() {
      return streak.length
    },
    get error() {
      return error
    },
    stop: () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId)
      if (fakeTimer !== null) clearInterval(fakeTimer)
    },
  }
}
